/**
 * ColorGradient is a widget that provides the ability
 * for an end-user to select a color along a color spectrum.
 *
 * @module mw-colorpicker/ColorGradient
 * @mixes  dijit/_WidgetBase
 * @mixes  dijit/_TemplatedMixin
 * @mixes  dijit/_WidgetsInTemplateMixin
 *
 * @example <caption>Simple MATLAB Color Gradient</caption>
 * var colorGradient = new ColorGradient();
 * colorGradient.on('change', function(e) {
 *      console.log(e.mwEventData.newValue);
 * });
 *

 * @copyright Copyright 2017 MathWorks, Inc.
 */
define([
    'dojo/_base/declare',
    'dojo/on',
    'dijit/_TemplatedMixin',
    'dijit/_WidgetBase',
    'dijit/_WidgetsInTemplateMixin',
    './MColor',
    'dojo/text!./templates/ColorGradient.html'
], function (declare, on, _TemplatedMixin, _WidgetBase,
    _WidgetsInTemplateMixin, MColor, template) {
    var colorGradient = declare([_WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin], {

        /**
         * @property {string} value - Hex value of the current color value
         * @instance
         */
        value: '#ff0000',

        _gradientClicked: false,
        _hueClicked: false,

        _GRADIENT_MARKER_RADIUS: 3,
        _HUE_MARKER_HEIGHT: 3,

        _hue: 0,
        _sat: 100,
        _val: 100,

        baseClass: 'mwColorGradient',

        templateString: template,

        postCreate: function () {
            this.inherited(arguments);
            this.own(
                on(this.gradientNode, 'mousedown', this._handleClickOnGradient.bind(this)),
                on(this.hueNode, 'mousedown', this._handleClickOnHue.bind(this))
            );
        },

        startup: function () {
            this.inherited(arguments);
            this._updateMarkers();
        },

        _setValueAttr: function (newColor) {
            var mcolor = new MColor(newColor);
            this._set('value', mcolor.toHex());

            var hsvColor = MColor.toHSV(mcolor.toRgb());
            this._set('_hue', hsvColor[0]);
            this._set('_sat', hsvColor[1]);
            this._set('_val', hsvColor[2]);
            this._updateMarkers();
            this._setBackgroundColor(MColor.fromHSV([hsvColor[0], 100, 100]));
        },

        /* Click event callbacks */

        _handleClickOnGradient: function (e) {
            if (e.button === 0) {
                this._gradientClicked = true;
                this._calculateDimensions();
                this._gradientRect = this.gradientNode.getBoundingClientRect();
                this._addMouseEventListeners();
            }
            e.preventDefault(); // Needed to avoid any possible dragging which blocks mouseup events
        },

        _handleClickOnHue: function (e) {
            if (e.button === 0) {
                this._hueClicked = true;
                this._calculateDimensions();
                this._hueRect = this.hueNode.getBoundingClientRect();
                this._addMouseEventListeners();
            }
            e.preventDefault(); // Needed to avoid any possible dragging which blocks mouseup events
        },

        _addMouseEventListeners: function () {
            this._mouseMoveHandler = this._handleMouseMove.bind(this);
            this._mouseUpHandler = this._handleMouseUp.bind(this);
            document.addEventListener('mousemove', this._mouseMoveHandler);
            document.addEventListener('mouseup', this._mouseUpHandler);
        },

        _handleMouseUp: function (e) {
            document.removeEventListener('mousemove', this._mouseMoveHandler);
            document.removeEventListener('mouseup', this._mouseUpHandler);

            // Emit change event once user releases the mouse
            var eventNameToEmit = 'change';
            if (this._gradientClicked) {
                this._gradientClicked = false;
                this._updateGradient(e, eventNameToEmit);
            } else if (this._hueClicked) {
                this._hueClicked = false;
                this._updateHue(e, eventNameToEmit);
            }
        },

        _handleMouseMove: function (e) {
            // Emit preview event as user drags the mouse
            var eventNameToEmit = 'preview';
            if (this._gradientClicked) {
                this._updateGradient(e, eventNameToEmit);
            }

            if (this._hueClicked) {
                this._updateHue(e, eventNameToEmit);
            }
        },

        /* Methods that update HSV value based on a click */

        _updateGradient: function (e, eventNameToEmit) {
            var newMarkerPos = this._calculateNewMarkerPosition(e, this._gradientRect);
            var sat = Math.round(((newMarkerPos.offsetX) / this._gradientRect.width) * 100);
            var val = Math.round(((this._gradientRect.height - newMarkerPos.offsetY) / this._gradientRect.height) * 100);

            this.set('_sat', sat);
            this.set('_val', val);
            this._setValueAndEmitEvent(eventNameToEmit);
            this._updateGradientMarker();
        },

        _updateHue: function (e, eventNameToEmit) {
            var newHueColor = this._calculateNewHueColor(e);
            this.set('_hue', newHueColor);
            this._setValueAndEmitEvent(eventNameToEmit);

            var mcolor = MColor.fromHSV([newHueColor, 100, 100]);
            this._setBackgroundColor(mcolor);
            this._updateHueMarker();
        },

        _calculateNewHueColor: function (e) {
            var newMarkerPos = this._calculateNewMarkerPosition(e, this._hueRect);
            var hueColor = Math.round(((newMarkerPos.offsetY) / this._hueRect.height) * 360);
            return hueColor;
        },

        _calculateNewMarkerPosition: function (e, rect) {
            var minX = rect.left;
            var minY = rect.top;
            var maxX = rect.left + rect.width;
            var maxY = rect.top + rect.height;

            /* If the mouse is horizontally out of the div bounds,
               specify the new X position on the closest edge */
            var newX = Math.min(Math.max(e.clientX, minX), maxX);

            /* If the mouse is vertically out of the div bounds,
               specify the new Y position on the closest edge */
            var newY = Math.min(Math.max(e.clientY, minY), maxY);

            /* Determine offset position (which is relative to the div) */
            var offsetX = newX - minX;
            var offsetY = newY - minY;
            return { offsetX: offsetX,
                offsetY: offsetY };
        },

        /* Methods that re-draw the markers on the gradient based on the current HSV value */

        _updateMarkers: function () {
            this._calculateDimensions();
            this._updateGradientMarker();
            this._updateHueMarker();
        },

        _updateGradientMarker: function () {
            var xPos = (this._width * this._sat / 100) - this._GRADIENT_MARKER_RADIUS;
            var yPos = this._height - (this._height * this._val / 100) - this._GRADIENT_MARKER_RADIUS;

            this.gradientMarkerNode.style.left = xPos + 'px';
            this.gradientMarkerNode.style.top = yPos + 'px';
        },

        _updateHueMarker: function () {
            var huePos = (this._hue / 360 * this._height) - this._HUE_MARKER_HEIGHT;
            this.hueMarkerNode.style.top = huePos + 'px';
        },

        /* Other helper methods */

        _setValueAndEmitEvent: function (eventName) {
            var oldValue = this.get('value');
            var h = this.get('_hue');
            var s = this.get('_sat');
            var v = this.get('_val');
            var mcolor = MColor.fromHSV([h, s, v]);
            var newValue = mcolor.toHex();

            this._set('value', newValue);
            this._emitEvent(eventName, oldValue, newValue);
        },

        _emitEvent: function (eventName, oldValue, newValue) {
            this.emit(eventName, {
                mwEventData: {
                    propertyName: 'value',
                    oldValue: oldValue,
                    newValue: newValue
                }
            });
        },

        _setBackgroundColor: function (mcolor) {
            this.gradientNode.style.backgroundColor = mcolor.toCss(false);
        },

        _calculateDimensions: function () {
            this._height = 88; // Height of .mwColorGradientSatVal - 2 to account for border
            this._width = 178; // Width of .mwColorGradientSatVal - 2 to account for border
            if (this.domNode.classList.contains('mwColorGradientWithStyle')) {
                this._height = 118;
            } else if (this.domNode.classList.contains('mwColorGradientWithStyles')) {
                this._height = 148;
            }
        }
    });

    return colorGradient;
});
